/*
 * ydm-type-switcher
 *  1. Fires the type_switcher_clicked analytics event when a shopper taps a sock-type
 *     tile (or a sticky-bar item). Navigation itself is a plain <a> full page load;
 *     this only emits the measurement signal. Channel is isolated here so the sink can
 *     be swapped in one place if the tracking build needs a different one.
 *  2. Optional sticky condensed switcher (opt-in per template): once the full tile row
 *     scrolls out of view, re-present the switcher as a slim titles-only bar pinned under
 *     the header, so the type navigation is never lost on a long collection. A further
 *     opt-in tucks the header away on scroll-down / restores it on scroll-up while the
 *     sticky bar is active.
 */
(function () {
  if (window.__ydmTypeSwitcherReady) return;
  window.__ydmTypeSwitcherReady = true;

  function emit(name, payload) {
    // Primary sink: dataLayer (GA4 / GTM friendly).
    try {
      window.dataLayer = window.dataLayer || [];
      var entry = { event: name };
      for (var k in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, k)) entry[k] = payload[k];
      }
      window.dataLayer.push(entry);
    } catch (e) {}
    // Bridge to a Shopify web pixel subscribing via analytics.subscribe(name, …).
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, payload);
      }
    } catch (e) {}
  }

  // One delegated handler covers both the main tiles and the cloned sticky-bar items:
  // any tile link carries data-ydm-type, and both the <nav> and the sticky bar carry the
  // switcher config (data-ydm-event / -context / -current).
  document.addEventListener(
    'click',
    function (event) {
      var tile = event.target.closest && event.target.closest('a[data-ydm-type]');
      if (!tile) return;
      var root = tile.closest('[data-ydm-event]');
      if (!root) return;
      emit(root.getAttribute('data-ydm-event') || 'type_switcher_clicked', {
        sock_type: tile.getAttribute('data-ydm-type') || '',
        sock_type_handle: tile.getAttribute('data-ydm-type-handle') || '',
        from_context: root.getAttribute('data-ydm-context') || '',
        from_type: root.getAttribute('data-ydm-current') || '',
        destination: tile.getAttribute('href') || ''
      });
    },
    true
  );

  // ---- Preserve shop mode across a tile click --------------------------------------
  // On the mixed all-socks PLP a shopper can be in "Ready-made packs" mode (?mode=packs,
  // mirrored onto <html data-ydm-mode> by ydm-plp-controls.js). The type-switcher tiles link
  // to plain type collections, so without this the first tile click dropped the shopper back
  // to mix mode. Keep every tile link carrying the CURRENT mode — dynamic, so it tracks a tab
  // toggle too, rather than the static per-tile setting which only ever means "always packs"
  // (that one stays reserved for the bundles template — see blocks/_ydm-type-tile.liquid).
  var docEl = document.documentElement;

  function currentMode() {
    var m = docEl.getAttribute('data-ydm-mode');
    if (m === 'mix' || m === 'packs') return m;
    try {
      var u = new URLSearchParams(window.location.search).get('mode');
      if (u === 'mix' || u === 'packs') return u;
    } catch (e) {}
    return null;
  }

  function decorateTileLinks(mode) {
    // Unknown mode (neither 'mix' nor 'packs') means we have no confident signal to act on —
    // most notably on a hide_tabs:true template (e.g. templates/collection.bundles.json), where
    // <ydm-plp-controls> never mounts, so data-ydm-mode is never set and a bare URL carries no
    // ?mode= either. On THAT template every tile's href is already correctly forced to packs
    // server-side via link_packs_mode (blocks/_ydm-type-tile.liquid) — link_packs_mode must win
    // unconditionally there. Touching hrefs on an unknown mode would strip that forced ?mode=packs
    // right back off, which is the opposite of "must still win where it is set". So: act only on
    // an explicit, recognized mode; leave hrefs exactly as server-rendered otherwise.
    if (mode !== 'mix' && mode !== 'packs') return;
    var links = document.querySelectorAll('a[data-ydm-type]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var href = a.getAttribute('href');
      if (!href || href === '#') continue;
      try {
        var url = new URL(href, window.location.origin);
        if (mode === 'packs') {
          url.searchParams.set('mode', 'packs');
        } else {
          url.searchParams.delete('mode');
        }
        a.setAttribute('href', url.pathname + url.search + url.hash);
      } catch (e) {}
    }
  }

  function syncTileMode() {
    decorateTileLinks(currentMode());
  }

  syncTileMode();
  // The attribute is the single source of truth (ydm-plp-controls sets it on load AND on every
  // toggle), so observing it covers the initial ?mode=packs landing and later tab switches alike.
  try {
    new MutationObserver(syncTileMode).observe(docEl, {
      attributes: true,
      attributeFilter: ['data-ydm-mode'],
    });
  } catch (e) {}
  document.addEventListener('ydm:plp_mode_switched', syncTileMode);

  // ---- Sticky condensed switcher ---------------------------------------------------

  function buildStickyBar(root) {
    var row = root.querySelector('.ydm-ts__row');
    if (!row) return null;

    var bar = document.createElement('div');
    bar.className = 'ydm-ts-sticky';
    // Carry the switcher config so clicks in the sticky bar emit the same event.
    bar.setAttribute('data-ydm-event', root.getAttribute('data-ydm-event') || 'type_switcher_clicked');
    bar.setAttribute('data-ydm-context', root.getAttribute('data-ydm-context') || '');
    bar.setAttribute('data-ydm-current', root.getAttribute('data-ydm-current') || '');
    bar.setAttribute('aria-hidden', 'true');
    bar.__ydmRoot = root;

    // The bar is appended to <body>, outside the switcher section's `.color-*` scope, so
    // copy the section's resolved scheme colours onto it — otherwise it tracks the page's
    // default scheme and can mismatch a switcher placed in a non-default colour scheme.
    try {
      var rootStyles = getComputedStyle(root);
      ['--color-background', '--color-foreground', '--color-foreground-rgb'].forEach(function (v) {
        var val = rootStyles.getPropertyValue(v);
        if (val) bar.style.setProperty(v, val.trim());
      });
    } catch (e) {}

    var nav = document.createElement('nav');
    nav.className = 'ydm-ts-sticky__row';
    nav.setAttribute('aria-label', root.getAttribute('aria-label') || 'Sock type');

    var tiles = row.querySelectorAll('.ydm-ts__tile');
    for (var i = 0; i < tiles.length; i++) {
      var tile = tiles[i];
      // The "All socks" tile stacks its label across two nodes (<b>All</b> + "socks");
      // join them with a space so the single-line sticky item reads "All socks".
      var allEl = tile.querySelector('.ydm-ts__all');
      var labelEl = tile.querySelector('.ydm-ts__label');
      var label;
      if (allEl) {
        label = Array.prototype.map
          .call(allEl.childNodes, function (n) { return n.textContent.trim(); })
          .filter(Boolean)
          .join(' ');
      } else if (labelEl) {
        label = labelEl.textContent.replace(/\s+/g, ' ').trim();
      } else {
        label = (tile.getAttribute('data-ydm-type') || '').trim();
      }
      if (!label) continue;

      var isCurrent = tile.classList.contains('is-current');
      var href = tile.getAttribute('href');
      var item;
      if (isCurrent || !href) {
        item = document.createElement('span');
        item.className = 'ydm-ts-sticky__item is-current';
        if (isCurrent) item.setAttribute('aria-current', 'page');
      } else {
        item = document.createElement('a');
        item.className = 'ydm-ts-sticky__item';
        item.href = href;
        item.setAttribute('data-ydm-type', tile.getAttribute('data-ydm-type') || label);
        item.setAttribute('data-ydm-type-handle', tile.getAttribute('data-ydm-type-handle') || '');
      }
      item.textContent = label;
      nav.appendChild(item);
    }

    if (!nav.children.length) return null;
    bar.appendChild(nav);
    document.body.appendChild(bar);
    return bar;
  }

  function setupSticky(root) {
    if (!('IntersectionObserver' in window)) return;

    // Idempotent: if this root was already wired (e.g. re-selected in the theme editor),
    // tear the previous instance down first so we never leak observers or orphan bars.
    if (typeof root.__ydmStickyTeardown === 'function') root.__ydmStickyTeardown();

    var bar = buildStickyBar(root);
    if (!bar) return;

    // Target the element that is actually sticky (the header-section wrapper in the
    // Horizon theme), so hiding it tucks the whole header band away cleanly rather than
    // leaving an empty sticky strip behind. Falls back to the component itself.
    var header = null;
    if (root.hasAttribute('data-ydm-sticky-header-hide')) {
      var headerComp = document.querySelector('#header-component');
      header = headerComp ? (headerComp.closest('.header-section') || headerComp) : null;
    }

    var visible = false;

    function setVisible(next) {
      if (next === visible) return;
      visible = next;
      bar.classList.toggle('is-visible', next);
      bar.setAttribute('aria-hidden', next ? 'false' : 'true');
      if (!next) {
        bar.classList.remove('at-top');
        if (header) header.classList.remove('ydm-ts-header-hidden');
      }
    }

    // Reveal the bar once the whole switcher has scrolled ABOVE the viewport top.
    var io = new IntersectionObserver(
      function (entries) {
        var e = entries[0];
        if (!e) return;
        setVisible(!e.isIntersecting && e.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    io.observe(root);

    var onScroll = null;
    if (header) {
      var lastY = window.pageYOffset || 0;
      var ticking = false;
      onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          var y = window.pageYOffset || 0;
          if (!visible) {
            lastY = y;
            return; // only manage the header while the sticky bar is up
          }
          if (y > lastY + 2) {
            header.classList.add('ydm-ts-header-hidden');
            bar.classList.add('at-top');
          } else if (y < lastY - 2) {
            header.classList.remove('ydm-ts-header-hidden');
            bar.classList.remove('at-top');
          }
          lastY = y;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    root.__ydmStickyTeardown = function () {
      io.disconnect();
      if (onScroll) window.removeEventListener('scroll', onScroll);
      if (header) header.classList.remove('ydm-ts-header-hidden');
      bar.remove();
      root.__ydmStickyTeardown = null;
    };
  }

  function initSticky(scope) {
    var el = scope && scope.querySelectorAll ? scope : document;
    var roots = el.querySelectorAll('[data-ydm-type-switcher][data-ydm-sticky]');
    for (var i = 0; i < roots.length; i++) setupSticky(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initSticky(); });
  } else {
    initSticky();
  }

  // Theme editor: sections are re-rendered on edit. Rebuild any switcher in the loaded
  // section, and clear bars whose root was removed with its section so none are orphaned.
  document.addEventListener('shopify:section:load', function (event) {
    var bars = document.querySelectorAll('.ydm-ts-sticky');
    for (var i = 0; i < bars.length; i++) {
      var b = bars[i];
      if (b.__ydmRoot && !document.contains(b.__ydmRoot)) {
        if (typeof b.__ydmRoot.__ydmStickyTeardown === 'function') b.__ydmRoot.__ydmStickyTeardown();
        else b.remove();
      }
    }
    initSticky(event.target);
    syncTileMode();
  });
})();
